// src/lib/financeService.ts
//
// BekalNanti — P2 Financial Reality Data Service
// Interacts with canonical schema: bekalnanti_app
// Handles CRUD for incomes, expenses, debts, savings, obligations.

import { supabase } from './supabase'
import {
  IncomeRecord,
  ExpenseRecord,
  DebtRecord,
  SavingRecord,
  ObligationRecord,
  FinancialRealityData,
} from '../journey/types'
import { summarizeFinancialReality } from './financialCalculation'

/**
 * Fetch all financial reality records for a household and compute summary.
 */
export async function fetchFinancialReality(householdId: string): Promise<FinancialRealityData> {
  const [
    { data: rawIncomes, error: incErr },
    { data: rawExpenses, error: expErr },
    { data: rawDebts, error: debtErr },
    { data: rawSavings, error: savErr },
    { data: rawObligations, error: oblErr },
  ] = await Promise.all([
    supabase.from('incomes').select('*').eq('household_id', householdId).order('created_at', { ascending: true }),
    supabase.from('expenses').select('*').eq('household_id', householdId).order('created_at', { ascending: true }),
    supabase.from('debts').select('*').eq('household_id', householdId).order('created_at', { ascending: true }),
    supabase.from('savings').select('*').eq('household_id', householdId).order('created_at', { ascending: true }),
    supabase.from('obligations').select('*').eq('household_id', householdId).order('created_at', { ascending: true }),
  ])

  if (incErr) throw new Error(`Gagal memuat penghasilan: ${incErr.message}`)
  if (expErr) throw new Error(`Gagal memuat pengeluaran: ${expErr.message}`)
  if (debtErr) throw new Error(`Gagal memuat cicilan/hutang: ${debtErr.message}`)
  if (savErr) throw new Error(`Gagal memuat tabungan: ${savErr.message}`)
  if (oblErr) throw new Error(`Gagal memuat kewajiban: ${oblErr.message}`)

  const incomes: IncomeRecord[] = (rawIncomes || []).map((i: any) => ({
    id: i.id,
    householdId: i.household_id,
    name: i.name,
    amount: Number(i.amount) || 0,
    currency: i.currency || 'IDR',
    frequency: i.frequency || 'monthly',
    isActive: i.is_active ?? true,
    sourceType: i.source_type || 'salary',
    effectiveFrom: i.effective_from,
  }))

  const expenses: ExpenseRecord[] = (rawExpenses || []).map((e: any) => ({
    id: e.id,
    householdId: e.household_id,
    name: e.name,
    amount: Number(e.amount) || 0,
    currency: e.currency || 'IDR',
    frequency: e.frequency || 'monthly',
    category: e.category || 'living',
    isEssential: e.is_essential ?? true,
    effectiveFrom: e.effective_from,
  }))

  const debts: DebtRecord[] = (rawDebts || []).map((d: any) => ({
    id: d.id,
    householdId: d.household_id,
    name: d.name,
    outstandingAmount: Number(d.outstanding_amount) || 0,
    monthlyPayment: Number(d.monthly_payment) || 0,
    currency: d.currency || 'IDR',
    interestRate: d.interest_rate !== null ? Number(d.interest_rate) : null,
    remainingMonths: d.remaining_months !== null ? Number(d.remaining_months) : null,
    isActive: d.is_active ?? true,
  }))

  const savings: SavingRecord[] = (rawSavings || []).map((s: any) => ({
    id: s.id,
    householdId: s.household_id,
    name: s.name,
    balance: Number(s.balance) || 0,
    currency: s.currency || 'IDR',
    purpose: s.purpose || 'education',
    isAvailableForGoal: s.is_available_for_goal ?? true,
  }))

  const obligations: ObligationRecord[] = (rawObligations || []).map((o: any) => ({
    id: o.id,
    householdId: o.household_id,
    name: o.name,
    amount: Number(o.amount) || 0,
    currency: o.currency || 'IDR',
    frequency: o.frequency || 'monthly',
    category: o.category || 'family_support',
    isActive: o.is_active ?? true,
  }))

  const summary = summarizeFinancialReality({
    incomes,
    expenses,
    debts,
    savings,
    obligations,
  })

  return {
    incomes,
    expenses,
    debts,
    savings,
    obligations,
    summary,
  }
}

// ----------------------------------------------------
// Incomes CRUD
// ----------------------------------------------------
export async function addIncome(
  householdId: string,
  item: {
    name: string
    amount: number
    frequency?: string
    sourceType?: string
    isActive?: boolean
  }
): Promise<IncomeRecord> {
  const { data, error } = await supabase
    .from('incomes')
    .insert({
      household_id: householdId,
      name: item.name.trim(),
      amount: item.amount,
      frequency: item.frequency || 'monthly',
      source_type: item.sourceType || 'salary',
      is_active: item.isActive ?? true,
      currency: 'IDR',
    })
    .select()
    .single()

  if (error) throw new Error(`Gagal menyimpan penghasilan: ${error.message}`)

  return {
    id: data.id,
    householdId: data.household_id,
    name: data.name,
    amount: Number(data.amount) || 0,
    currency: data.currency || 'IDR',
    frequency: data.frequency || 'monthly',
    isActive: data.is_active ?? true,
    sourceType: data.source_type || 'salary',
    effectiveFrom: data.effective_from,
  }
}

export async function deleteIncome(id: string): Promise<void> {
  const { error } = await supabase.from('incomes').delete().eq('id', id)
  if (error) throw new Error(`Gagal menghapus penghasilan: ${error.message}`)
}

// ----------------------------------------------------
// Expenses CRUD
// ----------------------------------------------------
export async function addExpense(
  householdId: string,
  item: {
    name: string
    amount: number
    frequency?: string
    category?: string
    isEssential?: boolean
  }
): Promise<ExpenseRecord> {
  const { data, error } = await supabase
    .from('expenses')
    .insert({
      household_id: householdId,
      name: item.name.trim(),
      amount: item.amount,
      frequency: item.frequency || 'monthly',
      category: item.category || 'living',
      is_essential: item.isEssential ?? true,
      currency: 'IDR',
    })
    .select()
    .single()

  if (error) throw new Error(`Gagal menyimpan pengeluaran: ${error.message}`)

  return {
    id: data.id,
    householdId: data.household_id,
    name: data.name,
    amount: Number(data.amount) || 0,
    currency: data.currency || 'IDR',
    frequency: data.frequency || 'monthly',
    category: data.category || 'living',
    isEssential: data.is_essential ?? true,
    effectiveFrom: data.effective_from,
  }
}

export async function deleteExpense(id: string): Promise<void> {
  const { error } = await supabase.from('expenses').delete().eq('id', id)
  if (error) throw new Error(`Gagal menghapus pengeluaran: ${error.message}`)
}

// ----------------------------------------------------
// Debts CRUD
// ----------------------------------------------------
export async function addDebt(
  householdId: string,
  item: {
    name: string
    outstandingAmount: number
    monthlyPayment: number
    remainingMonths?: number | null
    interestRate?: number | null
  }
): Promise<DebtRecord> {
  const { data, error } = await supabase
    .from('debts')
    .insert({
      household_id: householdId,
      name: item.name.trim(),
      outstanding_amount: item.outstandingAmount,
      monthly_payment: item.monthlyPayment,
      remaining_months: item.remainingMonths ?? null,
      interest_rate: item.interestRate ?? null,
      currency: 'IDR',
      is_active: true,
    })
    .select()
    .single()

  if (error) throw new Error(`Gagal menyimpan cicilan: ${error.message}`)

  return {
    id: data.id,
    householdId: data.household_id,
    name: data.name,
    outstandingAmount: Number(data.outstanding_amount) || 0,
    monthlyPayment: Number(data.monthly_payment) || 0,
    currency: data.currency || 'IDR',
    interestRate: data.interest_rate !== null ? Number(data.interest_rate) : null,
    remainingMonths: data.remaining_months !== null ? Number(data.remaining_months) : null,
    isActive: data.is_active ?? true,
  }
}

export async function deleteDebt(id: string): Promise<void> {
  const { error } = await supabase.from('debts').delete().eq('id', id)
  if (error) throw new Error(`Gagal menghapus cicilan: ${error.message}`)
}

// ----------------------------------------------------
// Savings CRUD
// ----------------------------------------------------
export async function addSaving(
  householdId: string,
  item: {
    name: string
    balance: number
    purpose?: string
    isAvailableForGoal?: boolean
  }
): Promise<SavingRecord> {
  const { data, error } = await supabase
    .from('savings')
    .insert({
      household_id: householdId,
      name: item.name.trim(),
      balance: item.balance,
      purpose: item.purpose || 'education',
      is_available_for_goal: item.isAvailableForGoal ?? true,
      currency: 'IDR',
    })
    .select()
    .single()

  if (error) throw new Error(`Gagal menyimpan tabungan: ${error.message}`)

  return {
    id: data.id,
    householdId: data.household_id,
    name: data.name,
    balance: Number(data.balance) || 0,
    currency: data.currency || 'IDR',
    purpose: data.purpose || 'education',
    isAvailableForGoal: data.is_available_for_goal ?? true,
  }
}

export async function deleteSaving(id: string): Promise<void> {
  const { error } = await supabase.from('savings').delete().eq('id', id)
  if (error) throw new Error(`Gagal menghapus tabungan: ${error.message}`)
}

// ----------------------------------------------------
// Obligations CRUD
// ----------------------------------------------------
export async function addObligation(
  householdId: string,
  item: {
    name: string
    amount: number
    frequency?: string
    category?: string
  }
): Promise<ObligationRecord> {
  const { data, error } = await supabase
    .from('obligations')
    .insert({
      household_id: householdId,
      name: item.name.trim(),
      amount: item.amount,
      frequency: item.frequency || 'monthly',
      category: item.category || 'family_support',
      currency: 'IDR',
      is_active: true,
    })
    .select()
    .single()

  if (error) throw new Error(`Gagal menyimpan kewajiban: ${error.message}`)

  return {
    id: data.id,
    householdId: data.household_id,
    name: data.name,
    amount: Number(data.amount) || 0,
    currency: data.currency || 'IDR',
    frequency: data.frequency || 'monthly',
    category: data.category || 'family_support',
    isActive: data.is_active ?? true,
  }
}

export async function deleteObligation(id: string): Promise<void> {
  const { error } = await supabase.from('obligations').delete().eq('id', id)
  if (error) throw new Error(`Gagal menghapus kewajiban: ${error.message}`)
}
